import React, {
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
  useCallback,
  useState,
  useEffect,
} from "react";

import { Box } from "@mui/material";
import ShipeeziGrid, { Column } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import { PurchaseOrder } from "../../../../models/purchase.order";
import { Searches } from "../../../../models/shipeezi.table";
import { paginate } from "../../../../util/paginate";
import { BookingOrderStatuses } from "../../../Booking/Maintenance/Orders/types";
import BadgePOStatus from "../../../Orders/StatusBadge";
import Modal from "../../Modal";
import { ShipmentOrderStatus } from "../Badge";
import { PurchaseOrderSearch, SearchAttribute } from "../types";
import useStyles from "./styles";

export type OrderIdsSelected = Array<number>;

export interface AddNewOrderHandles {
  open: (ordersIdsSelected: OrderIdsSelected) => void;
  close: () => void;
}
interface AddNewOrderProps {
  orderList: PurchaseOrder[];
  loading: boolean;
  gridColumns: Column[];
  onAddOrders: (orderSelecteds: PurchaseOrder[]) => void;
  onCancel?: () => void;
  showOrderStatus?: boolean;
}

const AddNewOrderModal: ForwardRefRenderFunction<AddNewOrderHandles, AddNewOrderProps> = (
  props,
  ref
) => {
  const { gridColumns, onAddOrders, orderList, loading, showOrderStatus } = props;

  const { t: translate } = useTranslation();
  const classes = useStyles();

  const [selecteds, setSelecteds] = useState<OrderIdsSelected>([]);
  const [modalOpened, setModalOpened] = useState(false);

  const [filteredOrderList, setFilteredOrderList] = useState<PurchaseOrder[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);

  const [searchAttributes, setSearchAttributes] = useState<SearchAttribute[]>([]);

  const filterOrders = (searchAttributes: SearchAttribute[]) => {
    return searchAttributes.reduce((filteredOrderList, searchAttribute) => {
      return filteredOrderList.filter((order: any) =>
        searchAttribute.values.some((value) => {
          switch (searchAttribute.attribute) {
            case PurchaseOrderSearch.CONSIGNEE_NAME:
              return String(order?.consignee?.name)?.toLowerCase().includes(value.toLowerCase());
            case PurchaseOrderSearch.SUPPLIER_NAME:
              return String(order?.supplier?.name)?.toLowerCase().includes(value.toLowerCase());
            default:
              return String(order[searchAttribute.attribute])
                ?.toLowerCase()
                .includes(value.toLowerCase());
          }
        })
      );
    }, orderList);
  };

  useEffect(() => {
    if (searchAttributes?.length > 0) {
      const filteredProducts = filterOrders(searchAttributes);

      setFilteredOrderList(paginate(filteredProducts, page, pageSize));
      setTotalItems(filteredProducts?.length);
    } else {
      setFilteredOrderList(paginate(orderList, page, pageSize));
      setTotalItems(orderList?.length);
    }
  }, [page, pageSize, searchAttributes, orderList]);

  const columns: Column[] = [
    ...gridColumns,
    {
      name: "productTotalQuantity",
      title: translate("totalQuantity"),
      formatText: (text?: string) => text || "-",
      hideSort: true,
      hideSearch: true,
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (value: string) => {
        if (showOrderStatus) return <BadgePOStatus value={value} />;

        return <ShipmentOrderStatus value={BookingOrderStatuses.READY} />;
      },
      hideSort: true,
      hideSearch: true,
    },
  ];

  const handleSearch = (searches: Searches) => {
    const attributes: SearchAttribute[] = [];

    if (searches) {
      searches.forEach((search) => {
        Object.getOwnPropertyNames(search).forEach((attribute) => {
          attributes.push({
            attribute,
            values: [search[attribute]],
          });
        });
      });
    }

    setSearchAttributes(attributes);
  };

  const openModal = useCallback((ordersIdSelected: OrderIdsSelected) => {
    setSelecteds(ordersIdSelected);
    setModalOpened(true);
  }, []);

  const closeModal = useCallback(() => {
    setSelecteds([]);
    setModalOpened(false);
    setPage(0);
    setPageSize(10);
    setSearchAttributes([]);
  }, []);

  useImperativeHandle(ref, () => ({
    open: openModal,
    close: closeModal,
  }));

  const renderSubtitle = () => (
    <span className={classes.subtitle}>{translate("selectOrdersBelowToAddThem")}.</span>
  );

  const onCallAddOrders = () => {
    const orders = orderList.filter((order) => selecteds.includes(order?.id || 0));

    orders.forEach((order) => {
      const products = order.purchaseOrderProducts;
      const lineSum = products?.reduce(
        (total, product) => total + Number(product.quantity ?? 0),
        0,
      );
      order.orderedQuantity =
        order.productTotalQuantity ??
        (products?.length ? lineSum : order.unitQuantity);
      order.totalQuantity = 0;
    });

    onAddOrders(orders);
    closeModal();
  };

  if (!modalOpened) return null;

  const handleSetPage = (page: number) => {
    setPage(page - 1);
  };

  return (
    <Modal
      open={modalOpened}
      handleClose={closeModal}
      showModalFooter
      showActionButtons
      actionButtonProps={{
        textSave: translate("add"),
        saveDisabled: !selecteds.length,
        showSaveButton: true,
        showCancelButton: true,
        onSave: onCallAddOrders,
        onCancel: closeModal,
      }}
      title={translate("addOrders")}
      className={classes.modal}
    >
      <Box className={classes.container}>
        <ShipeeziGrid
          data={filteredOrderList}
          totalItems={totalItems}
          columns={columns}
          loadingData={loading}
          emptyData={translate("noInformationToBeShown")}
          selecteds={selecteds}
          handleSelect={setSelecteds}
          pageSize={pageSize}
          currentPage={page}
          pageSizeList={[10, 30, 50, 100]}
          onChangePageSize={setPageSize}
          onNextPage={handleSetPage}
          onPreviousPage={handleSetPage}
          onGoToPage={handleSetPage}
          generalActions={{
            orderBy: {
              left: ["renderSubtitle"],
              right: ["handleSearch"],
            },
            renderSubtitle: renderSubtitle(),
            handleSearch: {
              onSearch: handleSearch,
            },
          }}
        />
      </Box>
    </Modal>
  );
};

export default forwardRef(AddNewOrderModal);
